import type { ColumnDef } from "@tanstack/react-table";
import { buildTagColumn } from "@/components/Table";
import type { TagType } from "@/components/Tag/Tag";
import { toDateTimeFormatWithTimezone } from "@/lib/dateHelpers";
import type { CallTopicRecentCall } from "../queries";
import { directionLabel, formatCallerIdentity, formatDuration } from "../_utils";

export type CallRow = CallTopicRecentCall & { id: string };

const SENTIMENT_TAG_TYPES: Record<string, TagType> = {
  positive: "success",
  negative: "error",
  neutral: "surface",
  mixed: "secondary"
};

function sentimentLabel(value: string | null): string | null {
  if (value == null || value === "") return null;
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function sentimentTagType(value: string | null): TagType {
  const key = (value ?? "").toLowerCase();
  return SENTIMENT_TAG_TYPES[key] ?? "surface";
}

export function getColumns(): ColumnDef<CallRow, unknown>[] {
  return [
    {
      accessorKey: "startedAt",
      header: "Started",
      cell: ({ row }) => toDateTimeFormatWithTimezone(row.original.startedAt),
      size: 200
    },
    {
      accessorKey: "direction",
      header: "Direction",
      cell: ({ row }) => directionLabel(row.original.direction),
      size: 110
    },
    {
      accessorKey: "durationSeconds",
      header: "Duration",
      cell: ({ row }) => formatDuration(row.original.durationSeconds),
      size: 110
    },
    buildTagColumn<CallRow>({
      accessorKey: "topic",
      header: "Topic",
      getTagLabel: (row) => row.topic,
      getTagType: () => "secondary",
      size: 180
    }),
    {
      accessorKey: "subtopic",
      header: "Subtopic",
      cell: ({ row }) => row.original.subtopic ?? "—",
      size: 180
    },
    {
      // Computed accessor (not the raw personName key): the table renders a
      // "--" placeholder for null accessor values without calling cell, so
      // the name/phone/unknown fallback must live in the accessed value.
      id: "personName",
      accessorFn: (row) => formatCallerIdentity(row),
      header: "Person",
      size: 160
    },
    {
      accessorKey: "workerName",
      header: "Team member",
      cell: ({ row }) => row.original.workerName ?? "—",
      size: 160
    },
    buildTagColumn<CallRow>({
      accessorKey: "sentimentLabel",
      header: "Sentiment",
      getTagLabel: (row) => sentimentLabel(row.sentimentLabel),
      getTagType: (row) => sentimentTagType(row.sentimentLabel),
      size: 130
    })
  ];
}
